import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '../../data/homeImages';
import { ArrowRight, Calculator, MapPin, Palette, Search, Users } from '../ui/Icons';

const SLIDE_INTERVAL = 6500;

/** Primary journeys, shown as a card bar that overlaps the bottom of the hero. */
const QUICK_LINKS = [
  { icon: Search, title: 'Paint finder', description: 'The right paint for every surface', link: '/products' },
  { icon: Palette, title: 'Colour catalogue', description: '1,800+ shades, curated series', link: '/colours/catalogue' },
  { icon: Calculator, title: 'Cost calculator', description: 'Quantity and budget in a minute', link: '/CalculateNow' },
  { icon: Users, title: 'Book a painter', description: 'Vertex-trained applicators', link: '/services/book-painter' },
  { icon: MapPin, title: 'Find a dealer', description: 'Genuine stock near you', link: '/store-locator' },
];

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(() =>
    typeof window !== 'undefined' && 'matchMedia' in window ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !('matchMedia' in window)) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
};

const Hero = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const slide = heroSlides[index];

  useEffect(() => {
    if (reducedMotion || paused || heroSlides.length < 2) return;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % heroSlides.length), SLIDE_INTERVAL);
    return () => window.clearInterval(id);
  }, [index, reducedMotion, paused]);

  return (
    <section className="relative bg-white">
      {/* ---------------- Full-bleed carousel ---------------- */}
      <div
        className="relative bg-brand-navy min-h-[720px] sm:min-h-0 sm:h-[700px] lg:h-[calc(100vh-76px)] lg:min-h-[680px] lg:max-h-[860px] overflow-hidden"
        role="region"
        aria-roledescription="carousel"
        aria-label="Vertex Paints highlights"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="absolute inset-0" aria-hidden="true">
          {heroSlides.map((s, i) => (
            <img
              key={s.image}
              src={s.image}
              alt=""
              loading={i === 0 ? 'eager' : 'lazy'}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1400ms] ease-premium ${i === index ? 'opacity-100 vp-pan' : 'opacity-0'}`}
            />
          ))}
          {/* Light scrim only — the copy sits in its own box, so the photo stays vivid. */}
          <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/45 via-brand-navy/10 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-brand-navy/60 to-transparent" />
        </div>

        <div className="relative min-h-[720px] sm:min-h-0 sm:h-full container flex items-end sm:items-center pt-60 sm:pt-0 pb-32 lg:pb-24">
          {/* Copy card */}
          <div key={index} className="vp-rise w-full max-w-xl bg-white/95 backdrop-blur-md rounded-3xl shadow-lift overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-brand-blue via-brand-red to-brand-gold" />
            <div className="p-6 sm:p-9 lg:p-10">
              <span className="vp-eyebrow">{slide.eyebrow}</span>
              <h1 className="mt-4 text-[2.1rem] sm:text-5xl lg:text-[3.35rem] font-bold leading-[1.05] tracking-tight text-brand-ink">
                {slide.headline[0]}
                <span className="block mt-1 vp-gradient-text">{slide.headline[1]}</span>
              </h1>
              <p className="mt-5 text-[15px] sm:text-base leading-relaxed text-brand-muted">{slide.lead}</p>
              <div className="mt-6 sm:mt-7 grid grid-cols-2 sm:flex gap-3">
                <Link to="/colours/catalogue" className="vp-btn vp-btn-primary">
                  Explore colours
                  <ArrowRight />
                </Link>
                <Link to="/products" className="vp-btn vp-btn-outline">
                  Shop the range
                </Link>
              </div>
              <dl className="hidden sm:grid mt-7 pt-6 border-t border-brand-line grid-cols-3 gap-4">
                {[
                  { v: '2007', l: 'Manufacturing since' },
                  { v: '1,800+', l: 'Shades on demand' },
                  { v: 'Award', l: 'PaintVision winner' },
                ].map((s) => (
                  <div key={s.l}>
                    <dt className="text-xl sm:text-2xl font-bold text-brand-blue leading-none">{s.v}</dt>
                    <dd className="mt-1.5 text-xs text-brand-muted leading-snug">{s.l}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>

        {/* Slide thumbnails — bottom right on desktop */}
        <div className="hidden lg:flex absolute right-8 xl:right-12 bottom-36 gap-3" role="group" aria-label="Choose a slide">
          {heroSlides.map((s, i) => (
            <button
              key={s.image}
              type="button"
              onClick={() => setIndex(i)}
              aria-pressed={i === index}
              aria-label={`Slide ${i + 1}: ${s.eyebrow}`}
              className={`group relative w-36 h-24 rounded-xl overflow-hidden border-2 transition-all duration-500 ${i === index ? 'border-brand-gold shadow-gold scale-105' : 'border-white/40 opacity-80 hover:opacity-100'}`}
            >
              <img src={/^\/images\/web\//.test(s.image) ? s.image.replace('.jpg', '-sm.jpg') : s.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <span className="absolute inset-0 bg-gradient-to-t from-brand-navy/85 to-transparent" />
              <span className="absolute left-2.5 bottom-2 right-2 text-left text-[11px] font-semibold text-white leading-tight">{s.eyebrow}</span>
              {i === index && !reducedMotion && !paused && (
                <span key={index} className="absolute left-0 top-0 h-1 bg-brand-gold animate-[vpProgress_6.5s_linear_forwards]" />
              )}
            </button>
          ))}
        </div>

        {/* Dots — mobile */}
        <div className="lg:hidden absolute top-6 right-5 flex gap-2">
          {heroSlides.map((s, i) => (
            <button
              key={s.image}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Slide ${i + 1}`}
              aria-pressed={i === index}
              className={`h-1.5 rounded-full transition-all ${i === index ? 'w-8 bg-brand-gold' : 'w-3 bg-white/60'}`}
            />
          ))}
        </div>
      </div>

      {/* ---------------- Quick-action bar, overlapping the image edge ---------------- */}
      <div className="relative z-10 -mt-24 lg:-mt-20 pb-2">
        <div className="container">
          <div className="grid grid-cols-2 lg:grid-cols-5 bg-white rounded-3xl shadow-lift overflow-hidden divide-x divide-y lg:divide-y-0 divide-brand-line">
            {QUICK_LINKS.map((q, i) => {
              const Icon = q.icon;
              return (
                <Link
                  key={q.title}
                  to={q.link}
                  className={`group flex items-center gap-3.5 p-4 sm:p-5 lg:p-6 hover:bg-brand-surface transition-colors ${i === QUICK_LINKS.length - 1 ? 'col-span-2 lg:col-span-1' : ''}`}
                >
                  <span className="flex-shrink-0 w-11 h-11 lg:w-12 lg:h-12 rounded-2xl bg-gradient-to-br from-brand-blue to-brand-red text-white flex items-center justify-center shadow-blue transition-transform duration-300 group-hover:scale-110">
                    <Icon size={20} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm lg:text-[15px] font-semibold text-brand-ink group-hover:text-brand-blue transition-colors">{q.title}</span>
                    <span className="hidden sm:block text-xs text-brand-muted leading-snug mt-0.5">{q.description}</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
